import React, { useState } from "react";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { useTranslation } from "react-i18next";
import TransactionVoidPopup from "./TransactionVoidPopup";
import TransactionRefundPopup from "./TransactionRefundPopup";

interface Transaction {
  id: number;
  amount: string;
  status: string;
  amount_refunded: string;
}

interface TransactionStatus {
  canVoid: boolean;
  canRefund: boolean;
  originalAmount: string;
  settlementStatus: string;
  transactionDetails: {
    [key: string]: any;
  };
}

interface TransactionActionsProps {
  transaction: Transaction;
  onActionComplete: () => void;
}

const TransactionActions: React.FC<TransactionActionsProps> = ({
  transaction,
  onActionComplete,
}) => {
  const [isVoidPopupOpen, setIsVoidPopupOpen] = useState(false);
  const [isRefundPopupOpen, setIsRefundPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [axiosInstance] = useAxiosInstance();
  const { t } = useTranslation();

  const checkTransactionStatus = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get<TransactionStatus>(
        `api/v1/transaction/${transaction.id}/check_status/`,
      );

      if (response.data.canVoid) {
        setIsVoidPopupOpen(true);
      } else if (response.data.canRefund) {
        setIsRefundPopupOpen(true);
      }
    } catch (error) {
      console.error("Error checking transaction status:", error);
    } finally {
      setLoading(false);
    }
  };

  if (
    transaction.amount_refunded !== "0.00" ||
    transaction.status.toLowerCase() !== "success"
  ) {
    return null;
  }

  return (
    <>
      <button
        className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600 disabled:opacity-50"
        onClick={checkTransactionStatus}
        disabled={loading}
      >
        {loading
          ? t("back.management.transaction.checking")
          : t("back.management.transaction.refund")}
      </button>

      {isVoidPopupOpen && (
        <TransactionVoidPopup
          transaction={transaction}
          onClose={() => setIsVoidPopupOpen(false)}
          onVoidComplete={() => {
            setIsVoidPopupOpen(false);
            onActionComplete();
          }}
        />
      )}

      {isRefundPopupOpen && (
        <TransactionRefundPopup
          transaction={transaction}
          onClose={() => setIsRefundPopupOpen(false)}
          onRefundComplete={() => {
            setIsRefundPopupOpen(false);
            onActionComplete();
          }}
        />
      )}
    </>
  );
};

export default TransactionActions;
